<template>
    <div class="purchase-modal" :class="{ 'is-pay-step': step === 'pay', 'is-expanded': paymentExpanded, 'is-success-step': step === 'success' }">
        <!-- :duration pins the leave/enter swap to an explicit JS timer instead of
             waiting on a transitionend event — that event doesn't reliably fire
             (backgrounded tab, prefers-reduced-motion, etc.), which left the old
             step's DOM (including the mounted Payment Element) stuck on screen
             indefinitely instead of being removed.
             mode="out-in" also means the 'pay' step's DOM isn't inserted until
             the 'select' step's leave transition finishes — @nextTick() alone
             resolves too early and mount() gets a null ref. @after-enter fires
             once the node is actually in the DOM, which is what mounting needs. -->
        <transition name="step-fade" mode="out-in" :duration="150" @after-enter="onStepEntered">
            <div v-if="step === 'select'" key="select" class="select-step">
                <div class="columns" v-if="!bundlesLoading && bundles.length">
                    <div class="item-wrapper column p-1" :key="bundle.id" v-for="(bundle, i) in bundles">
                        <purchase-item :bundle="bundle" :tier="tierFor(i)" :header-label="saleLabel(tierFor(i))" :buying="buyingId === bundle.id" @purchase="selectBundle" />
                    </div>
                </div>
                <b-loading :is-full-page="false" :active="bundlesLoading" />
                <p v-if="!bundlesLoading && !bundles.length" class="has-text-centered">Purchases aren't available right now — check back soon.</p>

                <h3 class="purchase-section-title">On a budget?</h3>
                <h6 class="purchase-section-subtitle">Here are some free ways to get credits</h6>
                <div class="info-card-wrapper columns">
                   <div class="column purchase-info-card-wrapper">
                        <div class="purchase-info-card">
                            <div class="purchase-info-image">
                                <div class="mini-discord">
                                    <div class="discord-typed-bubble">&daily</div>
                                    <div class="discord-reply">
                                        <img class="reply-avatar" :src="require('@/assets/images/brand/64w/pfp.png')" alt="" draggable="false">
                                        <div class="reply-body">
                                            <div class="reply-name">Gamebot <span class="bot-badge">BOT</span></div>
                                            <div class="reply-text">✅ Here's your daily reward — <b>+50 credits!</b></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="purchase-info-text">
                                Type <b>&daily</b> and vote for Gamebot on Top.gg every 12 hours to get {{ gold ? 'a chance at gold!' : 'guaranteed credits!'}}
                            </div>
                            <div class="has-text-right">
                                <a href="https://top.gg/bot/620307267241377793/vote" class="button is-info">Vote now!</a>
                            </div>
                        </div>
                    </div>
                   <div class="column purchase-info-card-wrapper">
                        <div class="purchase-info-card">
                            <div class="purchase-info-image">
                                <div class="mini-discord">
                                    <div class="discord-message-box">
                                        <div class="discord-invite-embed">
                                            <img class="invite-icon" :src="require('@/assets/images/brand/64w/pfp.png')" alt="" draggable="false">
                                            <div class="invite-body">
                                                <div class="invite-name">Gamebot Community</div>
                                                <div class="invite-meta"><span class="invite-dot online"></span>🎉 Giveaways &amp; games</div>
                                            </div>
                                            <div class="invite-join-pill">Join</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="purchase-info-text">
                                Join the community server for giveaways, games, and more!
                            </div>
                            <div class="has-text-right">
                                <a href="/discord?ref=purchase-modal" class="button is-discord">Join on Discord!</a>
                            </div>
                        </div>
                   </div>
                   <div class="column purchase-info-card-wrapper">
                        <div class="purchase-info-card">
                            <div class="purchase-info-image">
                                <div class="mini-discord">
                                    <div class="discord-command-suggestion">
                                        <span class="suggestion-icon">/</span>
                                        <div class="suggestion-text">
                                            <div class="suggestion-name">profile</div>
                                            <div class="suggestion-desc">View your Gamebot profile</div>
                                        </div>
                                    </div>
                                    <div class="discord-typed-bubble">/profile</div>
                                </div>
                            </div>
                            <div class="purchase-info-text">
                                Just play games! The more you play, the more rewards you unlock!
                            </div>
                            <div class="has-text-right">
                                <a href="/invite?ref=purchase-modal" class="button is-primary">Add Gamebot!</a>
                            </div>
                        </div>
                   </div>
                </div>
            </div>

            <div v-else-if="step === 'success'" key="success" class="success-step">
                <!-- Confetti sits behind the card content, absolutely
                     positioned, so it can't shift the layout as it animates. -->
                <div class="confetti-layer" aria-hidden="true">
                    <span
                        v-for="p in confetti" :key="p.id"
                        class="confetti-piece"
                        :style="{
                            left: p.left + '%',
                            backgroundColor: p.color,
                            width: p.size + 'px',
                            height: (p.size * 0.6) + 'px',
                            animationDelay: p.delay + 's',
                            animationDuration: p.duration + 's',
                            '--drift': p.drift + 'px',
                            '--spin': p.spin + 'deg',
                        }" />
                </div>

                <div class="success-body">
                    <div class="success-check" :class="'is-' + successState">
                        <svg viewBox="0 0 52 52" aria-hidden="true">
                            <circle class="check-ring" cx="26" cy="26" r="23" />
                            <!-- An exclamation rather than a tick when the
                                 credit is unconfirmed, so the icon doesn't
                                 assert something the copy is hedging. -->
                            <template v-if="successState === 'unverified'">
                                <path class="check-mark" d="M26 15 l0 16" />
                                <circle class="check-dot" cx="26" cy="37" r="2.4" />
                            </template>
                            <path v-else class="check-mark" d="M15 27 l8 8 l15 -16" />
                        </svg>
                    </div>

                    <h3 class="success-title">{{ successTitle }}</h3>

                    <!-- Mirrors the nav balance pill so the number they're
                         watching tick up is the same control they'll see in
                         the top bar once the modal closes. Hidden when we
                         couldn't reach the API — the count-up target is
                         optimistic, and showing an unverifiable balance as
                         fact is worse than showing none. -->
                    <div v-if="successState !== 'unverified'" class="success-balance-pill" :class="{ 'is-gold': gold }">
                        {{ displayBalance.toLocaleString() }}
                        <img class="currency-icon" :src="currencyIcon" alt="">
                    </div>

                    <p class="success-note">{{ successNote }}</p>

                    <p v-if="successState === 'unverified' && paymentIntentId" class="success-reference">
                        Reference: <code>{{ paymentIntentId }}</code>
                    </p>

                    <b-button type="is-primary" class="purchase-button success-done" @click="closeModal">
                        Done
                    </b-button>
                </div>
            </div>

            <div v-else key="pay" class="pay-step">
                <button class="back-icon-button" @click="!paying && backToSelect()" :disabled="paying" aria-label="Back to bundles">
                    <b-icon icon="arrow-left" pack="fas" size="is-small" />
                </button>
                <!-- Same card shell (circle icon, tier-colored header) as the
                     bundle grid — checkout happens inside the product card the
                     user just picked, not a differently-styled box next to it.
                     The header goes back to showing the literal quantity here
                     (rather than the sales pitch the grid shows) since this is
                     the only place that quantity is displayed once checkout
                     starts. -->
                <purchase-item :bundle="selectedBundle" :tier="selectedTier" :header-label="payHeaderLabel" class="pay-card">
                    <div ref="paymentElement" class="payment-element"></div>
                    <b-button
                        type="is-primary"
                        expanded
                        class="purchase-button pay-button"
                        :loading="paying"
                        :disabled="!elementsReady || !paymentComplete"
                        @click="pay">
                        Pay {{ formattedTotal }}
                    </b-button>
                </purchase-item>
            </div>
        </transition>
    </div>
</template>

<script>
import PurchaseItem from './PurchaseItem.vue'

export default {
  components: { PurchaseItem },
    name: 'PurchaseModal',
    data() {
        return {
            step: 'select', // 'select' | 'pay' | 'success'
            selectedBundle: null,
            selectedTier: 2,
            buyingId: null,
            elementsReady: false,
            // elementsReady only means the iframe finished loading — it says
            // nothing about whether the user has actually picked a method
            // and filled in valid details. Without this, Pay stayed fully
            // clickable-looking the instant the form appeared, before
            // anything in it was actually filled out.
            paymentComplete: false,
            paying: false,
            // True once the mounted Payment Element has visibly grown past
            // just showing its method tabs (e.g. the user picked "Card" and
            // its fields expanded) — widens the modal to match.
            paymentExpanded: false,
            // Success step. displayBalance is what the pill renders while
            // counting up; credited flips once the webhook's credit actually
            // shows up in the polled balance, and slowCredit marks the poll
            // window expiring without it (payment still succeeded — only
            // fulfillment is lagging).
            displayBalance: 0,
            credited: false,
            slowCredit: false,
            // Set when we can't reach the API to check the balance at all.
            // Distinct from slowCredit: there the credit is merely late,
            // here we have no idea whether it landed. Either way the
            // payment itself already succeeded.
            verifyFailed: false,
            // Kept so the unverified state can show the user something
            // support can actually look up.
            paymentIntentId: null,
            confetti: [],
        }
    },
    computed: {
        bundles() {
            return this.$store.getters['checkout/getBundles'](this.$store.state.purchase.modalItems)
        },
        bundlesLoading() {
            return this.$store.state.checkout.bundlesLoading
        },
        gold() {
            return this.$store.state.purchase.modalItems === 'gold'
        },
        formattedTotal() {
            if (!this.selectedBundle) return ''
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: this.selectedBundle.currency || 'usd',
            }).format(this.selectedBundle.unitAmount / 100)
        },
        // The pay card's header goes back to the literal quantity — it's the
        // only place that's shown once the value/unit line is hidden during
        // checkout, unlike the grid where the sales pitch replaces it.
        payHeaderLabel() {
            if (!this.selectedBundle) return ''
            return `${this.selectedBundle.quantity.toLocaleString()} ${this.selectedBundle.type === 'gold' ? 'gold' : 'credits'}`
        },
        // Same asset the nav balance pill uses, so the success pill reads as
        // the same control rather than a lookalike.
        currencyIcon() {
            return this.gold
                ? require('@/assets/images/currency/coin.gif')
                : require('@/assets/images/currency/128w/credits.png')
        },
        // The live balance from the store — what pollBalance() is watching to
        // decide whether the webhook's credit has actually landed.
        currentBalance() {
            const info = this.$store.state.dbInfo
            const value = this.gold ? info.gold : info.credits
            return typeof value === 'number' ? value : 0
        },
        selectedQuantityLabel() {
            if (!this.selectedBundle) return ''
            return `${this.selectedBundle.quantity.toLocaleString()} ${this.selectedBundle.type === 'gold' ? 'gold' : 'credits'}`
        },
        // Every one of these states is post-payment — Stripe already
        // confirmed the charge before this step renders. They differ only in
        // what we know about the *credit*, which is why none of them says
        // the purchase failed.
        successState() {
            if (this.credited) return 'credited'
            if (this.verifyFailed) return 'unverified'
            if (this.slowCredit) return 'slow'
            return 'pending'
        },
        successTitle() {
            // "Payment received" rather than "Purchase Successful" when we
            // can't confirm the credit — accurate about what we actually
            // know, without implying the money didn't leave their account.
            return this.successState === 'unverified' ? 'Payment received' : 'Purchase Successful'
        },
        successNote() {
            const currency = this.gold ? 'gold' : 'credits'
            switch (this.successState) {
                case 'credited':
                    return `${this.selectedQuantityLabel} added to your account.`
                case 'unverified':
                    return `Your payment went through, but we couldn't reach your account to confirm the ${currency} landed. They usually arrive within a minute — if they don't, contact support with the reference below.`
                case 'slow':
                    return `Your ${currency} are on the way — they'll appear shortly.`
                default:
                    return 'Adding to your account…'
            }
        },
        prefersReducedMotion() {
            return typeof window !== 'undefined' &&
                window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches
        },
    },
    methods: {
        tierFor(index) {
            if (index === 0) return 1
            if (index === this.bundles.length - 1) return 3
            return 2
        },
        // The grid's header would otherwise just repeat the quantity that's
        // already shown right below it — a short pitch reads better there.
        saleLabel(tier) {
            if (tier <= 1) return 'STARTER'
            if (tier <= 2) return 'GREAT VALUE'
            return 'BEST VALUE'
        },
        async selectBundle(bundle) {
            // The purchase modal is reachable while logged out (e.g. the
            // Shop page's "Get more credits" banner) — send them to log in
            // instead of silently failing the checkout request.
            if (!this.$store.getters.getToken) {
                this.$emit('close')
                this.$router.push('/login')
                return
            }

            this.buyingId = bundle.id
            this.selectedTier = this.tierFor(this.bundles.findIndex(b => b.id === bundle.id))
            const clientSecret = await this.$store.dispatch('checkout/startCheckout', bundle)
            this.buyingId = null
            if (!clientSecret) return

            // Mounting happens from onStepEntered() once the 'pay' step's DOM
            // is actually inserted — with mode="out-in" that's later than
            // $nextTick() resolves, so mounting eagerly here could hand
            // Stripe a ref that doesn't exist in the DOM yet.
            this._pendingClientSecret = clientSecret
            this.selectedBundle = bundle
            this.step = 'pay'
        },
        onStepEntered() {
            // Same reason the Payment Element mounts from here rather than
            // $nextTick: with mode="out-in" the step's DOM isn't inserted
            // until the previous one finishes leaving, and the count-up needs
            // the pill on screen before it starts.
            if (this.step === 'success') {
                this.runSuccessAnimation()
                return
            }
            if (this.step === 'pay' && this._pendingClientSecret) {
                const clientSecret = this._pendingClientSecret
                this._pendingClientSecret = null
                this.mountPaymentElement(clientSecret)
            }
        },
        async mountPaymentElement(clientSecret) {
            const stripe = await this.$store.dispatch('checkout/init')
            // Match Stripe's own background/theme to the card it's sitting
            // in — the white default look jarring dropped into the dark
            // gold-mode card otherwise. colorPrimary is deliberately left at
            // Stripe's default (not our teal/gold) — tinting the form's own
            // focus/selection accents to match read as noisy rather than
            // cohesive.
            const appearance = this.gold
                ? {
                    theme: 'night',
                    variables: {
                        colorBackground: '#30313D',
                        colorText: '#D9D9D8',
                        colorTextSecondary: '#9A9A98',
                        borderRadius: '9px',
                    },
                }
                : {
                    theme: 'stripe',
                    variables: {
                        borderRadius: '9px',
                    },
                }
            this._elements = stripe.elements({ clientSecret, appearance })
            this._paymentElement = this._elements.create('payment')
            this._paymentElement.mount(this.$refs.paymentElement)
            this._paymentElement.on('ready', () => { this.elementsReady = true })
            // Stripe's own signal for "every required field is filled in and
            // valid" — not just "a method is selected". Toggles back to
            // false the moment something becomes invalid/incomplete again.
            this._paymentElement.on('change', (event) => { this.paymentComplete = event.complete })
            this.observePaymentElementSize()
        },
        // Stripe's Payment Element is a cross-origin iframe, so it can't ask
        // our flexbox for more width the way normal content would — but it
        // does resize itself vertically as the user picks a payment method
        // and its fields expand. Watching that height (rather than any
        // Stripe-specific event) is what lets the modal widen to match,
        // instead of staying narrow while the content underneath it balloons.
        observePaymentElementSize() {
            if (typeof ResizeObserver === 'undefined' || !this.$refs.paymentElement) return
            this._resizeObserver = new ResizeObserver(entries => {
                // ~280px is roughly "just the collapsed method tabs" for this
                // account's enabled methods (Card/Bank/Afterpay/Klarna) —
                // comfortably past that means some method's fields expanded.
                this.paymentExpanded = entries[0].contentRect.height > 280
            })
            this._resizeObserver.observe(this.$refs.paymentElement)
        },
        async pay() {
            this.paying = true

            const stripe = await this.$store.dispatch('checkout/init')
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements: this._elements,
                confirmParams: {
                    return_url: `${window.location.origin}${this.$route.path}`,
                },
                redirect: 'if_required',
            })

            this.paying = false

            // A real payment failure — nothing was charged, so this stays on
            // the pay step for another attempt rather than moving forward.
            // A snackbar rather than a permanent element in the modal — the
            // card details are still right there for a retry, so the error
            // doesn't need to occupy space once it's been seen.
            if (error) {
                this.$buefy.snackbar.open({
                    message: error.message,
                    type: 'is-danger',
                })
                return
            }

            this.paymentIntentId = paymentIntent ? paymentIntent.id : null
            this.purchaseSucceeded()
        },
        purchaseSucceeded() {
            this.$gtag.event('purchase', {
                event_category: 'ecommerce',
                event_label: this.selectedBundle.id,
                value: this.selectedBundle.quantity,
            })

            // The count-up runs against the balance as it was *before* this
            // purchase, up to what it'll be once credited. The payment has
            // already succeeded and the bundle's quantity is fixed, so the
            // target is known here — pollBalance() then reconciles against
            // what the webhook actually wrote.
            this._countFrom = this.currentBalance
            this._countTo = this.currentBalance + this.selectedBundle.quantity
            this.displayBalance = this._countFrom
            this.credited = false
            this.slowCredit = false
            this.confetti = []

            this.pollBalance()
            // The animation itself kicks off from onStepEntered, once this
            // step's DOM is actually on screen.
            this.step = 'success'
        },
        runSuccessAnimation() {
            const from = this._countFrom || 0
            const to = this._countTo || 0

            if (this.prefersReducedMotion) {
                this.displayBalance = to
                return
            }

            const duration = 1400
            const start = performance.now()
            // Ease-out cubic: fast at first, visibly settling as it lands on
            // the final number rather than stopping dead.
            const ease = t => 1 - Math.pow(1 - t, 3)

            const finish = () => {
                if (this._isDestroyed || this.step !== 'success') return
                if (this._countRaf) cancelAnimationFrame(this._countRaf)
                clearTimeout(this._countFallback)
                this._countRaf = null
                this._countFallback = null
                this.displayBalance = to
                this.fireConfetti()
            }

            const tick = now => {
                if (this._isDestroyed || this.step !== 'success') return
                const t = Math.min((now - start) / duration, 1)
                this.displayBalance = Math.round(from + (to - from) * ease(t))
                if (t < 1) {
                    this._countRaf = requestAnimationFrame(tick)
                } else {
                    finish()
                }
            }
            this._countRaf = requestAnimationFrame(tick)
            // requestAnimationFrame is paused entirely while the tab is in the
            // background, so without this the number would sit on the old
            // balance (and confetti would never fire) for anyone who switched
            // away mid-purchase. setTimeout is only throttled, not stopped.
            this._countFallback = setTimeout(finish, duration + 400)
        },
        // Confetti is generated rather than hardcoded so the burst differs
        // each time; the CSS keyframes handle the actual fall.
        fireConfetti() {
            if (this.prefersReducedMotion || this.verifyFailed) return
            const colors = ['#52D9E9', '#E952D1', '#79eac1', '#EBD26C', '#5865F2']
            const pieces = []
            for (let i = 0; i < 44; i++) {
                pieces.push({
                    id: i,
                    left: Math.random() * 100,
                    color: colors[i % colors.length],
                    size: 6 + Math.random() * 6,
                    delay: Math.random() * 0.5,
                    duration: 1.8 + Math.random() * 1.4,
                    drift: (Math.random() - 0.5) * 120,
                    spin: 180 + Math.random() * 540,
                })
            }
            this.confetti = pieces
        },
        // App.vue binds @close on the <b-modal>, not on this component, so
        // $emit('close') from here had no listener and the modal just stayed
        // open — closing has to go through the store.
        closeModal() {
            this.$store.commit('setPurchaseModalOpen', false)
            // Let the modal's own close transition finish before resetting
            // the step, so the grid doesn't flash behind the fade-out.
            setTimeout(() => this.backToSelect(), 300)
        },
        // Fulfillment happens server-side via webhook, which is usually near
        // -instant but is genuinely async — give it a few chances to land
        // rather than claiming credit we haven't actually seen. Bails out if
        // the component goes away mid-poll.
        async pollBalance() {
            const target = this._countTo
            for (let i = 0; i < 5; i++) {
                await new Promise(resolve => setTimeout(resolve, 1500))
                if (this._isDestroyed) return
                try {
                    await this.$store.dispatch('fetchDBInfo')
                } catch (err) {
                    // fetchDBInfo swallows the network error and then throws
                    // on res.json(), and this was called without a .catch —
                    // so an unreachable API left the modal claiming "adding
                    // to your account…" forever. The payment itself already
                    // went through, so this is a *verification* failure, not
                    // a failed purchase: telling the user it failed here
                    // would invite a second payment for the same thing.
                    // eslint-disable-next-line no-console
                    console.error(err)
                    this.verifyFailed = true
                    // The count-up finishes slightly before the first poll
                    // resolves, so confetti may already be falling — clear
                    // it rather than celebrating over a warning.
                    this.confetti = []
                    return
                }
                if (this.currentBalance >= target) {
                    this.credited = true
                    return
                }
            }
            // Reachable, just hasn't landed yet — the webhook is lagging.
            this.slowCredit = true
        },
        // Tears down the mounted Payment Element before leaving the pay
        // step — Vue removing the container div isn't enough on its own,
        // Stripe needs .destroy() called on the element instance or its
        // iframe/listeners can linger.
        destroyPaymentElement() {
            if (this._resizeObserver) {
                this._resizeObserver.disconnect()
                this._resizeObserver = null
            }
            if (this._paymentElement) {
                this._paymentElement.destroy()
                this._paymentElement = null
            }
            this._elements = null
            this.paymentExpanded = false
            this.elementsReady = false
            this.paymentComplete = false
        },
        backToSelect() {
            this.destroyPaymentElement()
            this._pendingClientSecret = null
            this.step = 'select'
            this.selectedBundle = null
            this.$store.dispatch('checkout/resetCheckout')
        },
    },
    mounted() {
        this.$store.dispatch('checkout/fetchBundles')
    },
    beforeDestroy() {
        this._isDestroyed = true
        if (this._countRaf) cancelAnimationFrame(this._countRaf)
        clearTimeout(this._countFallback)
        this.destroyPaymentElement()
    },
}
</script>

<style lang="scss" scoped>
.purchase-modal {
    padding: 20px;
    background-color: rgba(238, 244, 244, 0.90);
    max-width: 900px;
    margin: 0 auto;
    backdrop-filter: blur(10px);
    border-radius: 12px;
    transition: max-width 0.15s ease;

    &.is-pay-step {
        max-width: 380px;
        // The product card itself now carries all the visual weight (white
        // background, shadow, rounded corners) — the modal's own frosted
        // background/padding just doubled that up as a box-around-a-box.
        background: none;
        backdrop-filter: none;
        padding: 0;
        // A payment method's expanded fields (e.g. Card) push Stripe's own
        // internal layout to lay things out more horizontally given more
        // room, instead of forcing everything into one narrow tall column.
        &.is-expanded {
            max-width: 720px;
            // The modal growing alone doesn't help — the card inside it
            // (which is what actually contains the Payment Element) has to
            // grow too, or Stripe still only ever gets 320px to lay out in.
            .pay-card {
                max-width: 640px;
            }
        }
    }

    // Same treatment as the pay step — the success card carries its own
    // white surface, so the modal's frosted panel would just double it up.
    &.is-success-step {
        max-width: 420px;
        background: none;
        backdrop-filter: none;
        padding: 0;
    }

    .success-step {
        position: relative;
        // Confetti falls past the card's bounds; clipping it here keeps the
        // burst inside the card instead of over the whole page.
        overflow: hidden;
        border-radius: 12px;
        background-color: white;
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.15);
    }
    .success-body {
        position: relative;
        // Sits above .confetti-layer so the number stays readable.
        z-index: 1;
        text-align: center;
        padding: 2rem 1.5rem 1.5rem;
    }
    .success-check {
        width: 64px;
        height: 64px;
        margin: 0 auto 0.75rem;
        svg { width: 100%; height: 100%; }
        .check-ring {
            fill: none;
            stroke: #38d06d;
            stroke-width: 3;
            opacity: 0.35;
        }
        .check-mark {
            fill: none;
            stroke: #38d06d;
            stroke-width: 5;
            stroke-linecap: round;
            stroke-linejoin: round;
            // Drawn on via dash offset — the path length is ~34 units, so a
            // slightly larger dash covers it fully before the sweep starts.
            stroke-dasharray: 40;
            stroke-dashoffset: 40;
            animation: check-draw 0.45s ease-out 0.1s forwards;
        }
        .check-dot {
            fill: #38d06d;
        }
        // Amber, not red — the payment succeeded, so this is a caution
        // about what we could confirm, not an error.
        &.is-unverified {
            .check-ring { stroke: #E0A800; }
            .check-mark { stroke: #E0A800; }
            .check-dot { fill: #E0A800; }
        }
    }
    .success-reference {
        margin-top: 0.5rem;
        font-size: 0.75rem;
        color: #989EA7;
        code {
            font-size: 0.72rem;
            background: rgba(0, 0, 0, 0.05);
            padding: 0.1rem 0.35rem;
            border-radius: 4px;
            // Support needs to be able to read this back exactly.
            user-select: all;
            word-break: break-all;
        }
    }
    .success-title {
        font-size: 1.4rem;
        font-weight: bold;
        color: #646978;
        margin-bottom: 1rem;
    }
    // Deliberately mirrors NavBalance's pill so the number counting up here
    // reads as the same control the user will see in the top bar after.
    .success-balance-pill {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 2rem;
        font-weight: bold;
        color: #32a6de;
        background-color: white;
        border: 2px solid rgba(50, 166, 222, 0.25);
        border-radius: 9999px;
        padding: 0.4rem 1.25rem;
        // Tabular figures stop the pill from jittering horizontally as the
        // digits change during the count-up.
        font-variant-numeric: tabular-nums;
        // No .is-gold rule here on purpose — gold always renders inside
        // is-dark-mode, and a scoped rule would out-specify that override
        // (scoped selectors pick up a [data-v] attribute), leaving the pill
        // on the dimmer light-mode gold against the dark card.
        .currency-icon {
            height: 1.75rem;
            width: auto;
        }
    }
    .success-note {
        margin-top: 0.75rem;
        color: #989EA7;
        font-size: 0.85rem;
        min-height: 1.2rem;
    }
    .success-done {
        margin-top: 1.25rem;
        border: none;
    }
    .confetti-layer {
        position: absolute;
        inset: 0;
        pointer-events: none;
        overflow: hidden;
    }
    .confetti-piece {
        position: absolute;
        top: -12px;
        border-radius: 2px;
        // --drift and --spin are set per piece inline so each one falls on
        // its own path; the shared keyframes just consume them.
        animation-name: confetti-fall;
        animation-timing-function: ease-in;
        animation-fill-mode: forwards;
    }

    .purchase-section-title {
        font-size: 1.5rem;
        font-weight: bold;
        text-align: center;
        color: #646978;
    }
    .purchase-section-subtitle {
        font-size: 0.75rem;
        font-weight: bold;
        text-align: center;
        color: #989EA7;
        margin-bottom: 1rem;
    }
    .purchase-info-card-wrapper {
        padding: 5px;
        display: flex;
    }
    // display:flex + flex:1 make every card in the row fill the row's
    // (already equal, via Bulma's default flex-stretch columns) height —
    // margin-top: auto on .has-text-right then pins each card's CTA to the
    // bottom regardless of how much text sits above it, so "Vote now!",
    // "Join on Discord!", and "Add Gamebot!" line up horizontally instead
    // of trailing whatever height each card's own content happened to add
    // up to.
    .purchase-info-card {
        border-radius: 9px;
        padding: 15px;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
        background-color: white;
        color: #9A9A98;
        display: flex;
        flex-direction: column;
        flex: 1;

        .purchase-info-text {
            text-align: left;
            margin-bottom: 5px;
        }

        .has-text-right {
            margin-top: auto;
            padding-top: 5px;
        }

        // Small "Discord chat" mockups replacing static screenshots — same
        // fixed-height treatment for all three cards is what actually keeps
        // them the same size, since a real screenshot's height depended on
        // whatever that image's own aspect ratio happened to be.
        .purchase-info-image {
            margin-bottom: 10px;
            .mini-discord {
                background-color: $discord-background-primary;
                border-radius: 8px;
                padding: 10px 12px;
                // A fixed (not min) height is what actually keeps all three
                // mockups the same size regardless of how much content each
                // one has — min-height alone still let the shorter ones (no
                // reply row, no suggestion row) end up visibly shorter.
                height: 104px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 6px;
                text-align: left;
            }
            .discord-message-box {
                background-color: $discord-channeltextarea-background;
                border-radius: 6px;
                padding: 8px 10px;
            }
            .discord-typed-bubble {
                background-color: $discord-channeltextarea-background;
                color: $discord-interactive-active;
                border-radius: 6px;
                padding: 6px 10px;
                font-size: 0.8rem;
            }
            .discord-command-suggestion {
                display: flex;
                align-items: center;
                gap: 8px;
                background-color: $discord-background-secondary;
                border-radius: 6px;
                padding: 6px 8px;
                .suggestion-icon {
                    flex-shrink: 0;
                    width: 20px;
                    height: 20px;
                    border-radius: 4px;
                    background-color: $discord;
                    color: white;
                    font-weight: bold;
                    font-size: 0.75rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .suggestion-name {
                    color: white;
                    font-size: 0.8rem;
                    font-weight: 600;
                }
                .suggestion-desc {
                    color: $discord-channels-default;
                    font-size: 0.65rem;
                }
            }
            .discord-reply {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                .reply-avatar {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }
                .reply-name {
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .bot-badge {
                    background-color: $discord;
                    color: white;
                    font-size: 0.55rem;
                    font-weight: bold;
                    border-radius: 3px;
                    padding: 1px 4px;
                }
                .reply-text {
                    color: $discord-text-normal;
                    font-size: 0.75rem;
                }
            }
            .discord-invite-embed {
                display: flex;
                align-items: center;
                gap: 10px;
                .invite-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }
                .invite-body {
                    flex: 1;
                    min-width: 0;
                }
                .invite-name {
                    color: white;
                    font-weight: bold;
                    font-size: 0.8rem;
                }
                .invite-meta {
                    color: $discord-channels-default;
                    font-size: 0.7rem;
                }
                .invite-dot {
                    display: inline-block;
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    margin-right: 4px;
                    &.online { background-color: $success; }
                }
                .invite-join-pill {
                    flex-shrink: 0;
                    background-color: $success;
                    color: white;
                    font-size: 0.7rem;
                    font-weight: bold;
                    border-radius: 4px;
                    padding: 4px 10px;
                }
            }
        }
    }

    .pay-step {
        text-align: center;
    }
    .back-icon-button {
        border: none;
        background: white;
        border-radius: 100%;
        width: 2rem;
        height: 2rem;
        margin-bottom: 0.5rem;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #646978;
        transition: filter 0.2s;
        &:hover:not(:disabled) {
            filter: brightness(0.95);
        }
        &:disabled {
            cursor: not-allowed;
            opacity: 0.6;
        }
    }
    // The payment element/error/button live inside .purchase-content (the
    // same product-card shell PurchaseItem uses for the bundle grid), so
    // they only need their own spacing — the card already provides the
    // white background, border-radius, and shadow.
    .pay-card {
        max-width: 320px;
        margin: 0 auto;
        transition: max-width 0.15s ease;
        .payment-element {
            text-align: left;
            margin: 1rem 0;
        }
        .pay-button.purchase-button {
            border: none;
            font-size: 1rem;
        }
    }
}

// Fast (150ms) crossfade + slight rise between the select/pay steps —
// mode="out-in" keeps them from overlapping mid-transition.
.step-fade-enter-active,
.step-fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.step-fade-enter,
.step-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
}

@media (prefers-reduced-motion) {
    .step-fade-enter-active,
    .step-fade-leave-active {
        transition: opacity 0.15s ease;
    }
    .step-fade-enter,
    .step-fade-leave-to {
        transform: none;
    }
}

@keyframes check-draw {
    to { stroke-dashoffset: 0; }
}

// --drift and --spin come from each piece's inline style, so one set of
// keyframes covers every piece regardless of how many are generated.
@keyframes confetti-fall {
    0% {
        opacity: 1;
        transform: translate(0, 0) rotate(0deg);
    }
    100% {
        opacity: 0;
        transform: translate(var(--drift), 320px) rotate(var(--spin));
    }
}

@media (prefers-reduced-motion) {
    .purchase-modal .success-check .check-mark {
        animation: none;
        stroke-dashoffset: 0;
    }
}
</style>



<style lang="scss">
.purchase-modal.is-dark-mode {
    background-color: rgba(24,25,31,0.92);
    .purchase-item {
        .purchase-item-box {
            background-color: unset;
        }
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.15);
        .circle-icon, .circle-icon-holder, .purchase-content {
            background-color: #30313D;
            .purchase-value {
                color: $warning;
            }
        }
        .purchase-box {
            background-color: unset;
        }
        .purchase-button, .color-header {
            background-color: $warning;
            .purchase-title {
                color: #404040;
            }
        }
        .purchase-content .purchase-button {
            color: #404040;
        }
        &.is-basic {
            .color-header, .purchase-content .purchase-button {
                background-color: #F0F0F0;
            }
        }
        &.is-special {
            .color-header, .purchase-content .purchase-button {
                background-color: unset;
                background: linear-gradient(101.67deg, #E952D1 0%, #EBD26C 100%);
            }
        }
    }
    .purchase-section-title {
        color: #D9D9D8;
    }
    .purchase-section-subtitle {
        color: #9A9A98;
    }
    .purchase-info-card {
        background-color: #30313D;
    }
    .back-icon-button {
        background: #30313D;
        color: #D9D9D8;
    }
    // Gold mode's success card follows the same dark surface the rest of the
    // gold UI uses, rather than staying white.
    .success-step {
        background-color: #30313D;
    }
    .success-title {
        color: #D9D9D8;
    }
    .success-note {
        color: #9A9A98;
    }
    .success-reference {
        color: #9A9A98;
        code {
            background: rgba(255, 255, 255, 0.08);
            color: #D9D9D8;
        }
    }
    .success-balance-pill {
        background-color: #30313D;
        color: $warning;
        border-color: rgba(235, 210, 108, 0.4);
    }
    .success-done.purchase-button {
        background-color: $warning;
        color: #404040;
    }
}
</style>
